import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { debounce, isEqual } from 'lodash';
import { fetchShowcaseContacts } from '../api';
import AsyncSelect from 'react-select/async';
import { Button } from 'components/ui/button';
import { Card } from 'react-bootstrap-5';
import { faTimes, faTrash } from '@fortawesome/pro-solid-svg-icons';
import Row from './row';

import { reorderableList } from 'helpers/react_dnd/reorderable_list';

const paramsForGroup = group => ({
  id: group.id,
  projectContactsAttributes: group.projectContacts.map((contact, idx) => ({
    id: contact.id,
    sortOrder: idx,
  }))
});

function Group({ initialGroup, projectId, onDelete, onRemoveContact, onShowcaseContactSelect, onUpdateGroup }) {
  const [adding, setAdding] = useState(false);
  const [group, setGroup] = useState(initialGroup); // Create a copy for Drag-n-Drop
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setGroup(initialGroup);
    setDirty(false);
  }, [initialGroup]);

  const searchShowcaseContacts = debounce((inputValue, callback) => {
    fetchShowcaseContacts({ searchText: inputValue }).then((response) => {
      const responseContacts = Array.isArray(response) ? response : [];
      const existingContacts = group.projectContacts;
      callback(
        responseContacts.filter(contact => !existingContacts.find(psc => psc.showcaseContactId === contact.id))
      );
    });
  }, 250);

  const handleReorder = (dragItemId, hoverIndex) => {
    setDirty(true);

    const updatedContacts = [...group.projectContacts];
    const dragItemIndex = updatedContacts.findIndex(contact => contact.id === dragItemId);
    const dragItem = updatedContacts[dragItemIndex];
    updatedContacts.splice(dragItemIndex, 1);
    updatedContacts.splice(hoverIndex, 0, dragItem);

    setGroup({ ...group, projectContacts: updatedContacts });
  };


  const handleSaveGroup = () => {
    if (!dirty) return;
    const payload = paramsForGroup(group);

    if (isEqual(payload, paramsForGroup(initialGroup))) {
      // Reset dirty state if no changes were made
      setDirty(false);
      return;
    }

    onUpdateGroup(group.id, paramsForGroup(group));
  };

  return (
    <div className="mt-3" key={group.name}>
      <div className="d-flex justify-content-between align-items-center m-0">
        <small className="fw-semibold mb-0 text-uppercase">{group.name}</small>
        <div>
          {!adding && (
            <Button label="+ Add Member" size="sm" variant="ghost" onClick={() => setAdding(true)} />
          )}
          {!group.default && (
            <Button
              aria-label="Delete group"
              className="text-destructive"
              icon={faTrash}
              size="sm"
              variant="ghost"
              onClick={onDelete}
            />
          )}
        </div>
      </div>
      {adding && (
        <div className="d-flex w-100 align-items-center my-2">
          <AsyncSelect
            className="flex-grow-1"
            getOptionLabel={contact => `${contact.firstName} ${contact.lastName}`}
            getOptionValue={contact => contact.id}
            loadOptions={searchShowcaseContacts}
            placeholder="Add contact by name, email, or job title..."
            value="" // Clear text upon selection
            onChange={contact => onShowcaseContactSelect(contact, group.id)}
          />
          <Button
            aria-label="Cancel adding member"
            className="text-destructive"
            icon={faTimes}
            size="sm"
            variant="ghost"
            onClick={() => setAdding(false)}
          />
        </div>
      )}
      {group.projectContacts.length === 0 ? (
        <Card className="mt-2">
          <Card.Body className="text-muted">
            No showcase contacts in this group yet.
          </Card.Body>
        </Card>
      ) : group.projectContacts.map((contact, index) => (
        <Row
          contact={contact}
          index={index}
          key={contact.id}
          projectId={projectId}
          type={`SHOWCASE_CONTACT_${group.id}`}
          onRemoveContact={onRemoveContact}
          onReorder={handleReorder}
          onReorderSave={handleSaveGroup}
        />
      ))}
    </div>
  );
}

Group.propTypes = {
  initialGroup: PropTypes.shape({
    name: PropTypes.string.isRequired,
    projectContacts: PropTypes.array.isRequired,
  }).isRequired,
  projectId: PropTypes.number.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRemoveContact: PropTypes.func.isRequired,
  onShowcaseContactSelect: PropTypes.func.isRequired,
  onUpdateGroup: PropTypes.func.isRequired
};

export default reorderableList(Group);
